/* eslint-disable no-console */

import React from 'react';

import { BrowserClient, Hub, Severity } from '@sentry/browser';
import { FormattedMessage } from 'react-intl';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { isFedRamp } from '@atlaskit/atlassian-context/is-fedramp';
import type { CreateFlagArgs, DismissFn } from '@atlaskit/flag/flag-provider';
import ErrorIcon from '@atlaskit/icon/core/status-error';
import { token } from '@atlaskit/tokens';
import { type AnalyticsAttributes, fireOperationalAnalytics } from '@atlassian/analytics-bridge';
import { maskEmailIds } from '@atlassian/proforma-utils/sanitizer';
import { Action, ActionSubject } from '@atlassian/proforma-utils/analytics/events';

import { Notices, type NoticeType } from '../../components/Notice/noticeTypes';
import { type ApiError, Environment } from '../../models';
import { type ErrorUtils, type ReportErrorOptions } from '../ErrorUtils';

// structured tags that we send to Sentry
// everything else from details object will go into "extras"
const TAGS = ['module', 'actionSubject', 'url', 'isProformaLocalConsumption'];
// From https://sentry.io/settings/atlassian-2y/projects/proforma/keys/
const SENTRY_PUBLIC_KEY = 'c6ac6cc7ef554e3286ec09d412c9c1f4';
const SENTRY_PROJECT_ID = '6388218';
const SENTRY_COMMERCIAL_DSN = `https://${SENTRY_PUBLIC_KEY}@o55978.ingest.sentry.io/${SENTRY_PROJECT_ID}`;
const SENTRY_FEDRAMP_DSN = `https://${SENTRY_PUBLIC_KEY}@sentry.atlassian-us-gov-mod.com/${SENTRY_PROJECT_ID}`;
const PROFORMA_SENTRY_PROJECT_DSN = isFedRamp() ? SENTRY_FEDRAMP_DSN : SENTRY_COMMERCIAL_DSN;

export class LiveErrorUtils implements ErrorUtils {
	private readonly moduleName: string;
	private readonly showFlag: (args: CreateFlagArgs) => DismissFn;
	private readonly createAnalyticsEvent: CreateUIAnalyticsEvent;
	private readonly sentryHub: Hub | void;
	private readonly environment: Environment;

	public constructor(
		moduleName: string,
		showFlag: (args: CreateFlagArgs) => DismissFn,
		createAnalyticsEvent: CreateUIAnalyticsEvent,
		environment: Environment,
	) {
		this.moduleName = moduleName;
		this.showFlag = showFlag;
		this.createAnalyticsEvent = createAnalyticsEvent;
		this.environment = environment;
		this.sentryHub = this.getSentryHub(environment);
	}

	public reportError(error: Error | ApiError, options?: ReportErrorOptions): void {
		const errorPrefix = `Proforma(${this.moduleName}): `;
		const severity = options?.severity ? options.severity : Severity.Error;

		// 1) Log error in console
		switch (severity) {
			case Severity.Warning:
				console.warn(errorPrefix, error);
				break;
			case Severity.Info:
				console.info(errorPrefix, error);
				break;
			default:
				console.error(errorPrefix, error);
				break;
		}

		const actionSubject = options?.actionSubject ?? ActionSubject.PROFORMA_UI;

		const attributes: AnalyticsAttributes | undefined =
			options?.attributes &&
			Object.fromEntries(
				Object.entries(options.attributes).map(([key, value]) => [
					key,
					typeof value === 'string' ? maskEmailIds(value) : value,
				]),
			);

		// Todo: add user agent, package version, url,
		const details: Record<string, string | boolean | number | undefined> = {
			actionSubject,
			errorId: options?.id,
			severity,
			module: this.moduleName,
			errorMsg: (error as Error).message,
			errorInfo: options?.errorInfo,
			isClientFetchError: (error as ApiError).isClientFetchError,
			requestName: (error as ApiError).requestName,
			traceId: (error as ApiError).traceId,
			status: (error as ApiError).status,
			userAgent: this.getBrowserUserAgent(),
			url: this.getUrl(),
			...attributes,
		};

		// 2) Send error to Atlassian analytics pipeline (GasV3)
		fireOperationalAnalytics(
			this.createAnalyticsEvent({}),
			`${actionSubject} ${Action.FAILED}`,
			details,
		);

		// 3) Send error to Sentry
		if (this.environment === Environment.LOCAL || this.sentryHub === undefined) {
			return;
		}
		// oxlint-disable-next-line prefer-destructuring
		const sentryHub = this.sentryHub;
		sentryHub.withScope((scope): void => {
			scope.setLevel(severity);

			Object.keys(details).forEach((key) => {
				const value = details[key];
				if (value) {
					if (TAGS.includes(key)) {
						scope.setTag(key, value.toString());
					} else {
						scope.setExtra(key, value);
					}
				}
			});

			sentryHub.captureException(error);
		});
	}

	/**
	 * @private
	 * @deprecated notifyUser has been deprecated. Please use the `useFlags()` hook directly instead.
	 */
	public notifyUser(errorNotice: NoticeType, customDescription?: string): Promise<void> {
		const notice = Notices[errorNotice];
		if (notice) {
			this.showFlag({
				appearance: 'error',
				isAutoDismiss: true,
				icon: <ErrorIcon label="" color={token('color.icon.inverse')} />,
				title: <FormattedMessage {...notice.title} />,
				description: customDescription ? (
					customDescription
				) : (
					<FormattedMessage {...notice.message} />
				),
			});
			return Promise.resolve();
		} else {
			console.error(`Could not show error, notice type ${errorNotice} does not exist`);
			return Promise.reject();
		}
	}

	private getSentryHub(environment: Environment): Hub | void {
		try {
			const client = new BrowserClient({
				environment,
				dsn: PROFORMA_SENTRY_PROJECT_DSN,
			});
			return new Hub(client);
		} catch (error) {
			const actionSubject = 'getProformaSentryHub';
			const details: Record<string, string | boolean | undefined> = {
				actionSubject,
				severity: Severity.Error,
				module: this.moduleName,
				errorMsg: (error as Error).message,
				userAgent: this.getBrowserUserAgent(),
				url: this.getUrl(),
			};
			fireOperationalAnalytics(
				this.createAnalyticsEvent({}),
				`${actionSubject} ${Action.FAILED}`,
				details,
			);
		}
	}

	private getBrowserUserAgent(): string {
		return window?.navigator?.userAgent ?? 'unknown';
	}

	private getUrl(): string {
		return window?.document?.location?.host ?? 'unknown';
	}
}
